import { Fragment, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link, useParams } from 'react-router-dom';
import {
  apiErrorStatus,
  apiErrorText,
  apiLoadErrorText,
  useGetFieldDailyReportQuery,
  useGetFieldSiteQuery,
  useGetFieldSiteMembersQuery,
  useGetSiteVolumeFactsQuery,
  useSetFieldReportStatusMutation,
  useDeleteFieldReportWorkMutation,
  useResendFieldVoiceReadbackMutation,
  useCreateRequestMutation,
} from '@shared/api';
import { useActiveProjectContext } from '@shared/lib/activeProjectContext';
import {
  canAcceptReport,
  canEditReportWeather,
  canReturnAcceptedReport,
  canReviewReport,
  factAcceptedQty,
  factNeedsReview,
  factReconcileFlags,
  factReconcileView,
  fmtFieldDate,
  isOwnReport,
  reportFactsSummary,
  reportLineDecision,
  reportLineFact,
  reportStatusLabel,
  reportStatusTone,
  siteReadOnly,
  useCurrentUserId,
  useFieldAccess,
  useSiteForeman,
  voiceReadbackReason,
  voiceReadbackSkipText,
  volumeFactStatusLabel,
  volumeFactStatusTone,
  workProvenanceLabel,
} from '@entities/field';
import { Card } from '@shared/ui/Card';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { Table } from '@shared/ui/Table';
import { ProjectInBinBanner } from './ProjectInBinBanner';
import { ReportWeather } from './ReportWeather';
import { PersonalReportWorkForm, PersonalReportPhotoUpload } from './PersonalReportEntry';
import { ReportPhotos } from './ReportPhotos';
import { canWritePersonalReport, reportAuthorLabel } from '@entities/field/tituls';

const BackLink = styled(Link)`
  display: inline-block;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-decoration: none;
  margin-bottom: ${({ theme }) => theme.spacing(4)};

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const Title = styled.h2`
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.01em;
`;

const HeadRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
`;

const Meta = styled.div`
  font-size: 13.5px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: ${({ theme }) => theme.spacing(2)};
  line-height: 1.6;
`;

const Section = styled.section`
  margin-top: ${({ theme }) => theme.spacing(8)};
`;

const SectionLabel = styled.div`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const Muted = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
`;

const ReadbackRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

const TranscriptBox = styled(Card)`
  font-size: 14px;
  line-height: 1.65;
  white-space: pre-wrap;
`;

const TableWrap = styled(Card)`
  padding: 0;
  overflow-x: auto;
`;

const NumCell = styled.td`
  text-align: right;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

const Actions = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
  margin-top: ${({ theme }) => theme.spacing(5)};
`;

const ReturnBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
  flex: 1;
  min-width: 280px;
`;

const CommentArea = styled.textarea`
  width: 100%;
  min-height: 72px;
  padding: ${({ theme }) => theme.spacing(2.5)} ${({ theme }) => theme.spacing(3.5)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-size: 14px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const ErrorNote = styled.div.attrs({ role: 'alert' })`
  color: ${({ theme }) => theme.colors.status.danger};
  font-size: 13px;
`;

/** Причина возврата — заметно, а не строкой мелкого шрифта в шапке. */
const ReturnNote = styled(Card)`
  margin-top: ${({ theme }) => theme.spacing(4)};
  border-color: ${({ theme }) => theme.colors.status.danger};
  font-size: 14px;
  line-height: 1.6;
  white-space: pre-wrap;
`;

const FactLink = styled(Link)`
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 600;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

/** Ссылка-кнопка «к фактам рапорта» (прокрутка к разделу на этой же странице). */
const JumpLink = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const WarnNote = styled.div`
  color: ${({ theme }) => theme.colors.status.warning};
  font-size: 13.5px;
  line-height: 1.55;
`;

/** id раздела фактов рапорта — цель «к фактам». */
const FACTS_SECTION_ID = 'report-facts';

function scrollToFacts() {
  document.getElementById(FACTS_SECTION_ID)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

/** Остаток: число → строка с разумной точностью, null → прочерк. */
function fmtRemainder(value: number | null): string {
  if (value === null) return '—';
  const rounded = Math.round(value * 1000) / 1000;
  return String(rounded);
}

/**
 * Карточка сменного рапорта (`/agents/field/:siteId/reports/:reportId`) —
 * приёмка ПТО (СР-5): шапка с погодой смены (графа 3 ОЖР, [`ReportWeather`]:
 * проверяющий правит её в любом статусе, прораб — пока рапорт у него) +
 * транскрипт диктовки + строки работ с детерминированным остатком (план − факт,
 * сервер) и решением ПТО по факту каждой строки (КR4) + факты объёма, которые
 * рапорт породил при сдаче, + действия «Принять» / «Вернуть с комментарием».
 * Приёмку ведёт проверяющий ПТО и только у сданного (`confirmed`) рапорта, все
 * факты которого решены ([`canAcceptReport`]); свой рапорт проверяющий не
 * принимает (КR11). Принятый рапорт возвращается прорабу, только если после его
 * последней сдачи ПТО отклонил объёмы (`return_allowed`, [`canReturnAcceptedReport`]).
 * Возврат уведомляет автора (N-2). Проект ПТО площадки в корзине — карточка
 * только для чтения.
 */
export function FieldReportDetail() {
  const { siteId, reportId } = useParams<{ siteId: string; reportId: string }>();
  const id = reportId ?? '';
  const {
    currentData: data,
    isLoading,
    isError,
    error: loadError,
  } = useGetFieldDailyReportQuery(id, {
    skip: !reportId,
  });
  const report = data?.report;
  const site = report?.site_id ?? siteId ?? '';
  const membersQ = useGetFieldSiteMembersQuery(site, { skip: !site });
  // Факты объёма рапорта: сервер материализует их при сдаче (draft → confirmed).
  const {
    data: factsResp,
    isLoading: factsLoading,
    isError: factsError,
    error: factsLoadError,
  } = useGetSiteVolumeFactsQuery(site, { skip: !site });
  // Проект ПТО площадки в корзине — запись закрыта (сервер: 409).
  const { data: siteResp } = useGetFieldSiteQuery(site, { skip: !site });
  const readOnly = siteReadOnly(siteResp?.site);
  // Приёмка — проверяющему ПТО по проекту рапорта; зачитку чисел шлёт любой, кто
  // пишет на площадку (ведущий площадку или её прораб).
  const access = useFieldAccess(report?.project_id);
  const foreman = useSiteForeman(site);
  const reviewer = access.reviewer && !readOnly;
  const siteWriter = (access.manager || foreman) && !readOnly;
  // Свой рапорт проверяющий не принимает (КR11: заявил и принял — один человек).
  const userId = useCurrentUserId();
  const own = isOwnReport(report?.author_id, userId);
  const canEditOwn =
    !!report &&
    canWritePersonalReport({
      authorId: report.author_id,
      userId,
      status: report.status,
      siteWriter,
    });
  const { setActiveProjectId } = useActiveProjectContext();
  const [deleteWork, { isLoading: deletingWork }] = useDeleteFieldReportWorkMutation();
  const [setStatus, { isLoading: saving }] = useSetFieldReportStatusMutation();
  const [resendReadback, { isLoading: sendingReadback }] = useResendFieldVoiceReadbackMutation();
  const [createRequest, { isLoading: creatingReq }] = useCreateRequestMutation();
  const [comment, setComment] = useState('');
  const [showReturn, setShowReturn] = useState(false);
  // Ошибка действия; `facts` — причина в фактах рапорта (409 при приёмке/возврате):
  // рядом с текстом сервера — переход к фактам.
  const [error, setError] = useState<{ text: string; facts: boolean } | null>(null);
  // Рапорт уже был в этом статусе (страница устарела) — сервер ничего не менял.
  const [unchanged, setUnchanged] = useState<string | null>(null);
  const [readbackMsg, setReadbackMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [requestNotice, setRequestNotice] = useState<string | null>(null);

  useEffect(() => {
    const pid = report?.project_id ?? siteResp?.site.project_id;
    if (pid) setActiveProjectId(pid);
  }, [report?.project_id, siteResp?.site.project_id, setActiveProjectId]);

  async function onCreateCrossRequest(
    requestType: 'mto_supply' | 'pto_rd_clarification',
    titleText: string,
    extraPayload: Record<string, unknown>,
  ) {
    setError(null);
    setRequestNotice(null);
    try {
      const res = await createRequest({
        request_type: requestType,
        source_function: 'field',
        project_id: report?.project_id ?? siteResp?.site.project_id ?? undefined,
        site_id: site || undefined,
        title: titleText,
        priority: 'urgent',
        payload: {
          report_id: id,
          report_date: report?.shift_date,
          ...extraPayload,
        },
        submit: true,
      }).unwrap();
      setRequestNotice(`Создана и подана заявка № ${res.request.id.slice(0, 8)}: «${res.request.title}»`);
    } catch (e) {
      setError({
        text: apiErrorText(e, 'Не удалось создать межведомственную заявку.'),
        facts: false,
      });
    }
  }

  const works = [...(data?.works ?? [])].sort((a, b) => a.sort - b.sort);
  const resources = [...(data?.resources ?? [])].sort((a, b) => a.sort - b.sort);
  const machines = resources.filter((r) => r.kind === 'machine');
  const personnel = resources.filter((r) => r.kind === 'personnel');
  const usage = [...(data?.usage ?? [])].sort((a, b) => a.sort - b.sort);
  const notes = [...(data?.notes ?? [])].sort((a, b) => a.sort - b.sort);
  const facts = (factsResp?.facts ?? []).filter((f) => f.report_id === id);
  // Пока факты не загружены, считать нечего: решает сервер (409 с числом нерешённых).
  const summary = reportFactsSummary(facts);

  async function apply(status: 'accepted' | 'returned' | 'confirmed') {
    setError(null);
    setUnchanged(null);
    try {
      const res = await setStatus({
        id,
        status,
        comment: status === 'returned' ? comment.trim() || undefined : undefined,
      }).unwrap();
      if (res.unchanged) {
        setUnchanged(
          `Рапорт уже ${reportStatusLabel(res.report.status)} — изменений нет (страница была устаревшей).`,
        );
      }
      setShowReturn(false);
      setComment('');
    } catch (e) {
      setError({
        text: apiErrorText(e, 'Не удалось изменить статус рапорта.'),
        // 409 на приёмке — в рапорте есть нерешённые факты (сервер называет число).
        facts: status === 'accepted' && apiErrorStatus(e) === 409,
      });
    }
  }

  async function sendReadback() {
    setReadbackMsg(null);
    try {
      await resendReadback(id).unwrap();
      setReadbackMsg({
        ok: true,
        text: 'Зачитка чисел отправлена прорабу в VK — ждём кнопку «Верно».',
      });
    } catch (e) {
      // Не отправлено (409/422) — прежнее заверение сервер не трогает. Про VK ID
      // говорим, кто его задаёт (администратор), а не «привяжите в профиле».
      setReadbackMsg({
        ok: false,
        text:
          voiceReadbackSkipText(voiceReadbackReason(e)) ??
          apiErrorText(e, 'Не удалось отправить зачитку чисел.'),
      });
    }
  }

  // Рапорт у прораба: черновик или возвращённый ПТО на доработку.
  const atForeman = report?.status === 'draft' || report?.status === 'returned';
  // Вернуть принятый рапорт можно только за отклонения после последней сдачи
  // (`return_allowed`, КR4: рядом с рапортом в ответе или в самом рапорте); у
  // старого сервера поля нет — по числу отклонённых.
  const returnAllowed =
    typeof data?.return_allowed === 'boolean' ? data.return_allowed : report?.return_allowed;
  const returnAccepted =
    !!report && canReturnAcceptedReport(report.status, reviewer, summary.rejected, returnAllowed);
  // Плашка «верните рапорт» — когда вернуть есть за что (для прораба — тот же
  // признак сервера: исправлять есть что, ждём возврата ПТО).
  const returnWanted =
    !!report &&
    report.status === 'accepted' &&
    (typeof returnAllowed === 'boolean' ? returnAllowed : summary.rejected > 0);
  const canAccept = !!report && canReviewReport(report.status, reviewer) && !own;

  return (
    <>
      <BackLink to={`/agents/field/${site}`}>← К площадке</BackLink>

      {isLoading && <Muted>Загрузка рапорта…</Muted>}
      {isError && (
        <Muted>
          {apiLoadErrorText(
            loadError,
            'Не удалось загрузить рапорт.',
            'Нет доступа к рапорту: он виден участникам площадки и проверяющим ПТО проекта.',
          )}
        </Muted>
      )}

      {report && (
        <>
          <ProjectInBinBanner site={siteResp?.site} />
          <HeadRow>
            <Title>
              Рапорт {fmtFieldDate(report.shift_date)} ·{' '}
              {report.shift_type === 'night' ? 'ночная смена' : 'дневная смена'}
            </Title>
            <Badge $tone={reportStatusTone(report.status)}>
              {reportStatusLabel(report.status)}
            </Badge>
          </HeadRow>
          <Meta>
            {report.note && (
              <>
                {report.note}
                <br />
              </>
            )}
            Автор: {reportAuthorLabel(report, membersQ.currentData?.members ?? [], userId)}
            <br />
            Создан: {fmtFieldDate(report.created_at)}
          </Meta>
          {canEditOwn && (
            <>
              <PersonalReportWorkForm key={`work-${id}`} siteId={site} reportId={id} />
              <PersonalReportPhotoUpload key={`upload-${id}`} siteId={site} reportId={id} />
              <Button
                disabled={saving || works.length === 0}
                onClick={() => void apply('confirmed')}
              >
                {saving ? 'Сдаю…' : 'Сдать мой рапорт инженеру'}
              </Button>
              {works.length === 0 && <Meta>Добавьте выполненные работы перед сдачей рапорта.</Meta>}
            </>
          )}
          <ReportPhotos key={`photos-${id}`} siteId={site} reportId={id} />
          {/* Погода смены — графа 3 ОЖР: проверяющий ПТО правит её в любом статусе
              (КR8), писатель площадки — пока рапорт у прораба. */}
          <ReportWeather
            report={report}
            canEdit={canEditReportWeather(report.status, canEditOwn, reviewer)}
            reviewer={reviewer}
          />
          {report.return_comment && (
            <ReturnNote>
              <strong>
                {report.status === 'returned'
                  ? 'Возвращён на доработку:'
                  : 'Комментарий прошлого возврата:'}
              </strong>{' '}
              {report.return_comment}
            </ReturnNote>
          )}

          {atForeman && (
            <Section style={{ marginTop: 16 }}>
              <Muted>
                {report.status === 'draft'
                  ? 'Черновик — прораб ещё не сдал рапорт.'
                  : 'Рапорт у прораба на доработке — после исправления он сдаст его заново.'}
              </Muted>
              {report.voice_transcript && (
                <>
                  <ReadbackRow>
                    <Badge $tone={report.voice_numbers_confirmed ? 'success' : 'warning'}>
                      {report.voice_numbers_confirmed
                        ? 'Числа объёма подтверждены прорабом'
                        : 'Числа объёма ждут подтверждения прораба'}
                    </Badge>
                    {own && siteWriter && (
                      <Button
                        $variant="secondary"
                        disabled={sendingReadback}
                        onClick={sendReadback}
                      >
                        Отправить числа прорабу на подтверждение
                      </Button>
                    )}
                    {report.voice_readback_sent_at && (
                      <Muted>
                        Зачитка отправлена: {fmtFieldDate(report.voice_readback_sent_at)}
                      </Muted>
                    )}
                  </ReadbackRow>
                  {readbackMsg &&
                    (readbackMsg.ok ? (
                      <Muted style={{ marginTop: 8 }}>{readbackMsg.text}</Muted>
                    ) : (
                      <ErrorNote style={{ marginTop: 8 }}>{readbackMsg.text}</ErrorNote>
                    ))}
                </>
              )}
            </Section>
          )}

          {report.status === 'confirmed' && !reviewer && (
            <Section style={{ marginTop: 16 }}>
              <Muted>Рапорт сдан — ждёт приёмки проверяющим ПТО.</Muted>
            </Section>
          )}
          {report.status === 'accepted' && (
            <Section style={{ marginTop: 16 }}>
              <Muted>Рапорт принят ПТО — строки рапорта больше не меняются.</Muted>
              {returnWanted ? (
                <WarnNote style={{ marginTop: 8 }}>
                  {summary.rejected > 0
                    ? `Возвращённых ПТО объёмов: ${summary.rejected}`
                    : 'Есть возвращённые ПТО объёмы'}{' '}
                  — этот объём не принят и не попадёт в ОЖР.{' '}
                  {returnAccepted
                    ? 'Верните рапорт прорабу на доработку: он добавит исправленные строки и сдаст рапорт заново (отклонённая строка остаётся — её не исправить и не удалить).'
                    : 'Исправить объём прораб сможет, когда проверяющий ПТО вернёт рапорт.'}{' '}
                  <JumpLink type="button" onClick={scrollToFacts}>
                    К фактам рапорта ↓
                  </JumpLink>
                </WarnNote>
              ) : (
                summary.rejected > 0 && (
                  // Отклонения уже отработаны прорабом (были до последней сдачи) —
                  // повторно возвращать рапорт не за что (КR4).
                  <Muted style={{ marginTop: 8 }}>
                    Возвращённых ПТО объёмов: {summary.rejected} — в ОЖР они не попадут. Новых
                    возвратов после последней сдачи рапорта нет — возвращать рапорт не нужно.
                  </Muted>
                )
              )}
            </Section>
          )}

          {canReviewReport(report.status, reviewer) && own && (
            <Section style={{ marginTop: 12 }}>
              <WarnNote>
                Рапорт сдали вы — принять его может другой проверяющий ПТО: заявленное и принятое не
                подтверждает один человек.
              </WarnNote>
            </Section>
          )}

          {(canReviewReport(report.status, reviewer) || returnAccepted) && (
            <Actions>
              {canAccept && (
                <Button
                  $variant="primary"
                  disabled={
                    saving ||
                    factsLoading ||
                    !canAcceptReport(report.status, reviewer, summary.pending)
                  }
                  title={
                    summary.pending > 0
                      ? 'Сначала решите факты объёма рапорта: примите, поправьте или верните их'
                      : undefined
                  }
                  onClick={() => apply('accepted')}
                >
                  Принять рапорт
                </Button>
              )}
              {!showReturn && (
                <Button
                  $variant="secondary"
                  disabled={saving}
                  title={
                    report.status === 'accepted'
                      ? 'После последней сдачи ПТО отклонил объёмы: прораб добавит исправленные строки и сдаст рапорт заново'
                      : undefined
                  }
                  onClick={() => setShowReturn(true)}
                >
                  Вернуть на доработку…
                </Button>
              )}
              {showReturn && (
                <ReturnBox>
                  <CommentArea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder={
                      report.status === 'accepted'
                        ? 'Комментарий прорабу: какой объём заявить заново вместо отклонённого'
                        : 'Комментарий прорабу: что исправить'
                    }
                    aria-label="Комментарий возврата"
                  />
                  <Actions style={{ marginTop: 0 }}>
                    <Button
                      $variant="danger"
                      disabled={saving || !comment.trim()}
                      onClick={() => apply('returned')}
                    >
                      Вернуть с комментарием
                    </Button>
                    <Button
                      $variant="ghost"
                      disabled={saving}
                      onClick={() => {
                        setShowReturn(false);
                        setComment('');
                      }}
                    >
                      Отмена
                    </Button>
                  </Actions>
                </ReturnBox>
              )}
            </Actions>
          )}
          {canAccept && summary.pending > 0 && (
            <Section style={{ marginTop: 12 }}>
              <WarnNote>
                Принять рапорт можно, когда решены все его факты объёма: не решено —{' '}
                {summary.pending}. Примите, поправьте или верните их — иначе факт, возвращённый
                после приёмки, прораб уже не переподаст.{' '}
                <JumpLink type="button" onClick={scrollToFacts}>
                  К фактам рапорта ↓
                </JumpLink>
              </WarnNote>
            </Section>
          )}
          {unchanged && (
            <Section style={{ marginTop: 12 }}>
              <Muted>{unchanged}</Muted>
            </Section>
          )}
          {error && (
            <Section style={{ marginTop: 12 }}>
              <ErrorNote>
                {error.text}
                {error.facts && (
                  <>
                    {' '}
                    <JumpLink type="button" onClick={scrollToFacts}>
                      К фактам рапорта ↓
                    </JumpLink>
                  </>
                )}
              </ErrorNote>
            </Section>
          )}

          <Section>
            <SectionLabel>Работы за смену</SectionLabel>
            {works.length === 0 ? (
              <Muted>Строк работ нет.</Muted>
            ) : (
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <th>Наименование</th>
                      <th>Ед.</th>
                      <th style={{ textAlign: 'right' }}>План</th>
                      <th style={{ textAlign: 'right' }}>Факт</th>
                      <th style={{ textAlign: 'right' }}>Остаток</th>
                      <th>Источник</th>
                      <th>Решение ПТО</th>
                    </tr>
                  </thead>
                  <tbody>
                    {works.map((w) => {
                      // Решение ПТО по факту строки (КR4): видно, какую строку
                      // отклонили и почему; строка с решением заблокирована.
                      const decision = reportLineDecision(reportLineFact(w, facts), w.unit);
                      return (
                        <tr key={w.id}>
                          <td>
                            {w.work_item_code ? `${w.work_item_code} — ${w.name}` : w.name}
                            {canEditOwn && !decision?.locked && (
                              <Button
                                $variant="ghost"
                                $size="sm"
                                disabled={deletingWork}
                                onClick={async () => {
                                  if (!window.confirm('Удалить строку, чтобы внести исправленную?'))
                                    return;
                                  try {
                                    await deleteWork({ reportId: id, workId: w.id }).unwrap();
                                  } catch (e) {
                                    setError({
                                      text: apiErrorText(e, 'Не удалось удалить работу.'),
                                      facts: false,
                                    });
                                  }
                                }}
                              >
                                Удалить строку
                              </Button>
                            )}
                            {w.comment && <Meta style={{ marginTop: 4 }}>{w.comment}</Meta>}
                            {(w.titul_hint || w.marks_hint || w.drawing_hint) && (
                              <Meta style={{ marginTop: 4 }}>
                                {[
                                  w.titul_hint && `титул ${w.titul_hint}`,
                                  w.marks_hint && `марка ${w.marks_hint}`,
                                  w.drawing_hint && `черт. ${w.drawing_hint}`,
                                ]
                                  .filter(Boolean)
                                  .join(' · ')}
                              </Meta>
                            )}
                            {w.needs_review && (
                              <>
                                {' '}
                                <Badge $tone="warning">нужна проверка</Badge>
                              </>
                            )}
                          </td>
                          <td>{w.unit ?? '—'}</td>
                          <NumCell>{w.qty_plan ?? '—'}</NumCell>
                          <NumCell>{w.qty_fact ?? '—'}</NumCell>
                          <NumCell>{fmtRemainder(w.remainder)}</NumCell>
                          <td>
                            {workProvenanceLabel(w.provenance)}
                            {w.confidence !== null && w.confidence < 0.8 && (
                              <>
                                {' '}
                                <Badge $tone="warning">{Math.round(w.confidence * 100)}%</Badge>
                              </>
                            )}
                          </td>
                          <td>
                            {decision ? (
                              <>
                                <Badge
                                  $tone={decision.tone}
                                  title={
                                    decision.locked
                                      ? 'По строке есть решение ПТО — исправить или удалить её нельзя.'
                                      : undefined
                                  }
                                >
                                  {decision.label}
                                </Badge>
                                {decision.detail && (
                                  <Meta style={{ marginTop: 4 }}>{decision.detail}</Meta>
                                )}
                              </>
                            ) : (
                              '—'
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </TableWrap>
            )}
          </Section>

          <Section id={FACTS_SECTION_ID}>
            <SectionLabel>Факты объёма (сверка ПТО)</SectionLabel>
            {factsError ? (
              <Muted>
                {apiLoadErrorText(
                  factsLoadError,
                  'Не удалось загрузить факты объёма.',
                  'Нет доступа к фактам объёма этой площадки.',
                )}
              </Muted>
            ) : facts.length === 0 ? (
              <Muted>
                {report.status === 'draft'
                  ? 'Факты объёма появятся, когда прораб сдаст рапорт.'
                  : 'По этому рапорту фактов объёма нет.'}
              </Muted>
            ) : (
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <th>Наименование</th>
                      <th style={{ textAlign: 'right' }}>Заявлено</th>
                      <th style={{ textAlign: 'right' }}>Принято</th>
                      <th>Статус</th>
                      <th>Сверка с РД</th>
                    </tr>
                  </thead>
                  <tbody>
                    {facts.map((f) => {
                      const rv = factReconcileView(f.reconcile, { elementBound: !!f.element_id });
                      const accepted = factAcceptedQty(f);
                      return (
                        <tr key={f.id}>
                          <td>
                            <FactLink to={`/agents/field/${site}/volume-facts/${f.id}`}>
                              {f.work_item_code ? `${f.work_item_code} — ${f.name}` : f.name}
                            </FactLink>
                            {factNeedsReview(f) && (
                              <>
                                {' '}
                                <Badge $tone="warning">нужна проверка</Badge>
                              </>
                            )}
                            {factReconcileFlags(f.reconcile, f.status, { reviewer }).map((fl) => (
                              <Fragment key={fl.label}>
                                {' '}
                                <Badge $tone={fl.tone} title={fl.hint}>
                                  {fl.label}
                                </Badge>
                              </Fragment>
                            ))}
                          </td>
                          <NumCell>
                            {f.qty_raw} {f.unit ?? ''}
                          </NumCell>
                          <NumCell>
                            {f.status === 'accepted' && accepted !== null
                              ? `${fmtRemainder(accepted)} ${f.unit ?? ''}`
                              : '—'}
                          </NumCell>
                          <td>
                            <Badge $tone={volumeFactStatusTone(f.status)}>
                              {volumeFactStatusLabel(f.status)}
                            </Badge>
                          </td>
                          <td>
                            <Badge $tone={rv.tone}>{rv.label}</Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </TableWrap>
            )}
          </Section>

          {requestNotice && (
            <Section>
              <Badge $tone="success">{requestNotice}</Badge>
            </Section>
          )}

          {machines.length > 0 && (
            <Section>
              <SectionLabel>Техника</SectionLabel>
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <th>Техника</th>
                      <th style={{ textAlign: 'right' }}>Маш.-смены</th>
                      <th style={{ textAlign: 'right' }}>Моточасы</th>
                      <th style={{ textAlign: 'right' }}>Простой, ч</th>
                      <th>Причина простоя</th>
                      {siteWriter && <th>Заявка в смежный отдел</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {machines.map((m) => (
                      <tr key={m.id}>
                        <td>
                          {m.ref ? `${m.name} ${m.ref}` : m.name}
                          {m.needs_review && (
                            <>
                              {' '}
                              <Badge $tone="warning">нужна проверка</Badge>
                            </>
                          )}
                        </td>
                        <NumCell>{m.shifts ?? '—'}</NumCell>
                        <NumCell>{m.motohours ?? '—'}</NumCell>
                        <NumCell>{m.downtime_h ?? '—'}</NumCell>
                        <td>{m.downtime_reason ?? '—'}</td>
                        {siteWriter && (
                          <td>
                            {(m.downtime_h || m.downtime_reason) ? (
                              <Button
                                type="button"
                                $variant="secondary"
                                $size="sm"
                                disabled={creatingReq}
                                onClick={() =>
                                  void onCreateCrossRequest(
                                    'mto_supply',
                                    `Простой техники ${m.ref ? `${m.name} ${m.ref}` : m.name}: ${
                                      m.downtime_reason ?? `${m.downtime_h} ч`
                                    }`,
                                    {
                                      machine_resource_id: m.id,
                                      machine_name: m.name,
                                      downtime_h: m.downtime_h,
                                      downtime_reason: m.downtime_reason,
                                    },
                                  )
                                }
                              >
                                → В МТО / Логистику
                              </Button>
                            ) : (
                              '—'
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrap>
            </Section>
          )}

          {personnel.length > 0 && (
            <Section>
              <SectionLabel>Персонал</SectionLabel>
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <th>Специальность</th>
                      <th style={{ textAlign: 'right' }}>Численность</th>
                    </tr>
                  </thead>
                  <tbody>
                    {personnel.map((p) => (
                      <tr key={p.id}>
                        <td>
                          {p.name}
                          {p.needs_review && (
                            <>
                              {' '}
                              <Badge $tone="warning">нужна проверка</Badge>
                            </>
                          )}
                        </td>
                        <NumCell>{p.count ?? '—'}</NumCell>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrap>
            </Section>
          )}

          {usage.length > 0 && (
            <Section>
              <SectionLabel>Расход материалов</SectionLabel>
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <th>Материал</th>
                      <th style={{ textAlign: 'right' }}>Кол-во</th>
                      <th>Ед.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usage.map((u) => (
                      <tr key={u.id}>
                        <td>
                          {u.name}
                          {u.needs_review && (
                            <>
                              {' '}
                              <Badge $tone="warning">нужна проверка</Badge>
                            </>
                          )}
                        </td>
                        <NumCell>{u.qty ?? '—'}</NumCell>
                        <td>{u.unit ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrap>
            </Section>
          )}

          {notes.length > 0 && (
            <Section>
              <SectionLabel>Проблемы и заявки</SectionLabel>
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <th>Тип</th>
                      <th>Текст</th>
                      <th>Важность</th>
                      {siteWriter && <th>Межведомственная заявка</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {notes.map((n) => (
                      <tr key={n.id}>
                        <td>{n.kind === 'issue' ? 'проблема' : 'заявка'}</td>
                        <td>{n.text}</td>
                        <td>{n.severity ?? '—'}</td>
                        {siteWriter && (
                          <td>
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              <Button
                                type="button"
                                $variant="secondary"
                                $size="sm"
                                disabled={creatingReq}
                                onClick={() =>
                                  void onCreateCrossRequest('mto_supply', `Сменный рапорт: ${n.text}`, {
                                    note_id: n.id,
                                    note_kind: n.kind,
                                    severity: n.severity,
                                  })
                                }
                              >
                                → В МТО
                              </Button>
                              <Button
                                type="button"
                                $variant="ghost"
                                $size="sm"
                                disabled={creatingReq}
                                onClick={() =>
                                  void onCreateCrossRequest(
                                    'pto_rd_clarification',
                                    `Сменный рапорт: ${n.text}`,
                                    {
                                      note_id: n.id,
                                      note_kind: n.kind,
                                      severity: n.severity,
                                    },
                                  )
                                }
                              >
                                → В ПТО
                              </Button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrap>
            </Section>
          )}

          {report.voice_transcript && (
            <Section>
              <SectionLabel>Транскрипт диктовки (L-1)</SectionLabel>
              <TranscriptBox>{report.voice_transcript}</TranscriptBox>
            </Section>
          )}
        </>
      )}
    </>
  );
}
